import React from 'react';

import pluralize from 'pluralize';

import { AndroidIcon, AppleIcon, EvIcon } from '@evinced-private/ui-common';

import { SummaryPaneResult } from 'src/apps/mobile-common/types/CommonTypes';
import { MobileSummaryPaneItem } from 'src/common/interfaces/MobileSummaryPaneItem';
import { ProductType } from 'src/common/interfaces/Tenant';

export const createMobileSummaryItems = (
	summaryPaneData: SummaryPaneResult,
	productType: ProductType.MOBILE_SDK | ProductType.MOBILE_FLOW_ANALYZER
): MobileSummaryPaneItem[] => {
	const {
		totalScans,
		hasIosScans,
		hasAndroidScans,
		iosScans,
		androidScans,
		totalIssues,
		criticalIssues,
		averagePerScan,
		addedToJira
	} = summaryPaneData;
	const items: MobileSummaryPaneItem[] = [
		{
			label: 'Total Scans',
			value: [{ primary: totalScans }],
			ariaLabel: `Total scans: ${totalScans}`
		}
	];

	if (productType === ProductType.MOBILE_SDK) {
		const platformValues = [
			...(hasIosScans ? [{ primary: <EvIcon icon={AppleIcon} /> }] : []),
			...(hasAndroidScans ? [{ primary: <EvIcon icon={AndroidIcon} /> }] : [])
		];

		if (platformValues.length > 0) {
			const platforms = [];
			if (hasIosScans) {
				platforms.push('iOS');
			}
			if (hasAndroidScans) {
				platforms.push('Android');
			}

			items.push({
				label: 'Platform',
				value: platformValues,
				ariaLabel: `Mobile ${pluralize('platform', platforms.length)}: ${platforms.join(' and ')}`
			});
		}
	}

	if (productType === ProductType.MOBILE_FLOW_ANALYZER) {
		const platformScanValues = [
			...(hasIosScans ? [{ primary: iosScans, secondary: <EvIcon icon={AppleIcon} /> }] : []),
			...(hasAndroidScans
				? [{ primary: androidScans, secondary: <EvIcon icon={AndroidIcon} /> }]
				: [])
		];

		if (platformScanValues.length > 0) {
			const platformDetails = [];
			if (hasIosScans) {
				platformDetails.push(`${iosScans} iOS ${pluralize('scan', iosScans)}`);
			}
			if (hasAndroidScans) {
				platformDetails.push(`${androidScans} Android ${pluralize('scan', androidScans)}`);
			}

			items.push({
				label: 'Scans per platform',
				value: platformScanValues,
				ariaLabel: `Number of scans by platform: ${platformDetails.join(', ')}`
			});
		}
	}

	items.push(
		{
			label: 'Total Issues',
			value: [{ primary: totalIssues }],
			ariaLabel: `Total accessibility issues found: ${totalIssues}`
		},
		{
			label: 'Average per scan',
			value: [{ primary: averagePerScan, secondary: `${pluralize('issue', averagePerScan)}` }],
			ariaLabel: `Average number of issues per scan: ${averagePerScan} ${pluralize('issue', averagePerScan)}`
		},
		{
			label: 'Critical Issues',
			value: [{ primary: criticalIssues }],
			ariaLabel: `Number of critical severity issues: ${criticalIssues}`
		}
	);

	if (productType === ProductType.MOBILE_FLOW_ANALYZER) {
		items.push({
			label: 'Added to tickets',
			value: [{ primary: addedToJira }],
			ariaLabel: `Number of issues exported to tickets: ${addedToJira}`
		});
	}

	return items;
};
